/**
 * AttributionRoles.tsx
 *
 * Miroir exact de la logique Flutter :
 *   - fetchData()  → GET  /api/attribution-roles/data  (users + roles en parallèle)
 *   - assignRole() → POST /api/attribution-roles/assign
 *   - Filtre : si l'utilisateur connecté n'est pas root → pas de users/rôles root
 *   - URL de l'API lue via ParametreBi (url_backend_bi passé en prop ou lu depuis l'API)
 *
 * UX :
 *   - Liste déroulante des utilisateurs (Select)
 *   - Clic → pop-up (modal) avec infos user + Select rôle
 *   - Clic "Valider" → assignRole → snackbar succès/erreur
 *   - Liste mise à jour instantanément avec le nouveau rôle
 */

import { useState, useEffect, useCallback, useRef, CSSProperties } from "react";
import { assignAttributionRole, getAttributionData } from "../api";
import { getErrorMessage } from "../../../shared/api/http";
import { session } from "../../../shared/api/session";
import { colors } from "../../../shared/ui/tokens";

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface UserType {
  userId: string;
  fullName?: string;
  username?: string;
  Nom?: string;
  email?: string;
  idRole?: string;
  isActive?: number | boolean;
  photoUrl?: string;
  roleName?: string;
  depot?: string;        // ← dépôt de l'utilisateur
  idDepot?: string;      // ← identifiant du dépôt
  depotName?: string;    // ← nom du dépôt (alias possible)
  [key: string]: any;
}

export interface RoleType {
  idRole?: string;
  id?: string;
  nom?: string;
  name?: string;
  [key: string]: any;
}

interface SnackbarState {
  type: "success" | "error";
  message: string;
}

interface AssignResponse {
  success?: boolean;
  message?: string;
  [key: string]: any;
}

// ═══════════════════════════════════════════════════════════════
// CONSTANTES & UTILITAIRES
// ═══════════════════════════════════════════════════════════════

// Récupère le rôle de l'utilisateur connecté
function getCurrentUserRole(): string {
  try {
    const raw = localStorage.getItem("userRole") ?? sessionStorage.getItem("userRole") ?? "";
    return raw.toLowerCase().trim();
  } catch {
    return "";
  }
}

// Récupère le dépôt de l'utilisateur connecté (pour le filtre responsable boutique)
function getCurrentUserDepot(): string {
  try {
    const u = session.getUser<Record<string, any>>() ?? {};
    return (u?.depot ?? u?.depotName ?? u?.idDepot ?? u?.Depot ?? "").toString().trim();
  } catch {
    return "";
  }
}

// Vérifie si le rôle est "responsable boutique"
function isResponsableBoutique(role: string): boolean {
  return role
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .includes("responsable");
}

// Vérifie si le rôle est "vendeur"
function isVendeurRole(role: string): boolean {
  return role.toLowerCase().trim() === "vendeur";
}

// Couleurs inspirées du thème Flutter (AppColors)
const C = {
  primary:   colors.brand[700],   // violet
  primaryLt: colors.neutral[100],
  success:   colors.accent[500],
  error:     colors.danger[500],
  gray50:    colors.neutral[50],
  gray100:   colors.neutral[100],
  gray200:   colors.neutral[200],
  gray500:   colors.neutral[500],
  gray700:   colors.neutral[700],
  gray900:   colors.neutral[900],
  white:     colors.neutral[0],
} as const;

// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════

export default function AttributionRoles() {
  // ── State ──────────────────────────────────────────────────
  const [users,          setUsers]          = useState<UserType[]>([]);
  const [roles,          setRoles]          = useState<RoleType[]>([]);
  const [isLoading,      setIsLoading]      = useState<boolean>(false);
  const [errorMessage,   setErrorMessage]   = useState<string>("");
  const [searchQuery,    setSearchQuery]    = useState<string>("");

  // ── Rôle / dépôt de l'utilisateur connecté ────────────────
  const currentRole  = getCurrentUserRole();
  const currentDepot = getCurrentUserDepot();
  const isRespBoutique = isResponsableBoutique(currentRole);
  const isVendeur      = isVendeurRole(currentRole);

  // Modal
  const [selectedUser,   setSelectedUser]   = useState<UserType | null>(null);
  const [selectedRole,   setSelectedRole]   = useState<RoleType | null>(null);
  const [isSaving,       setIsSaving]       = useState<boolean>(false);
  const [modalOpen,      setModalOpen]      = useState<boolean>(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const snackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Snackbar ────────────────────────────────────────────────
  const showSnackbar = useCallback((type: SnackbarState["type"], message: string) => {
    if (snackTimer.current) clearTimeout(snackTimer.current);
    setSnackbar({ type, message });
    snackTimer.current = setTimeout(() => setSnackbar(null), 3500);
  }, []);

  // ── fetchData — miroir de fetchData() Flutter ───────────────
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      const data = await getAttributionData<UserType, RoleType>();
      if (!data || typeof data !== "object") {
        throw new Error("Réponse inattendue du serveur.");
      }
      if (!data.success) {
        throw new Error(data.message ?? "Erreur serveur.");
      }
      setUsers(data.users   ?? []);
      setRoles(data.roles   ?? []);
    } catch (e: any) {
      setErrorMessage(getErrorMessage(e, "Erreur de chargement.").replace("Exception: ", ""));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── filteredUsers — miroir du getter Flutter ────────────────
  // 1. Si responsable boutique → ne voir que les users de son dépôt
  // 2. Ensuite filtrer par recherche texte
  const depotFilteredUsers = isRespBoutique && currentDepot
    ? users.filter((u) => {
        const uDepot = (u.depot ?? u.depotName ?? u.idDepot ?? "").toString().trim();
        return uDepot === currentDepot;
      })
    : users;

  const filteredUsers = searchQuery.trim().length < 3
    ? depotFilteredUsers
    : depotFilteredUsers.filter((u) => {
        const q = searchQuery.toLowerCase();
        return (
          (u.fullName  ?? "").toLowerCase().includes(q) ||
          (u.username  ?? "").toLowerCase().includes(q) ||
          (u.nom       ?? "").toLowerCase().includes(q) ||
          (u.email     ?? "").toLowerCase().includes(q)
        );
      });

  // ── openUserDetail — miroir de openUserDetail() Flutter ─────
  const openUserDetail = useCallback((user: UserType) => {
    setSelectedUser(user);
    // Pré-sélectionner le rôle actuel de l'utilisateur
   const current = roles.find(
  (r) => r.idRole === user.idRole || r.nom === user.idRole || r.nom === user.nom
) ?? null;
    setSelectedRole(current);
    setModalOpen(true);
  }, [roles]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedUser(null);
    setSelectedRole(null);
  }, []);

  // ── assignRole — miroir de assignRole() Flutter ─────────────
  const assignRole = useCallback(async () => {
    if (!selectedUser || !selectedRole) {
      showSnackbar("error", "Veuillez sélectionner un utilisateur et un rôle.");
      return;
    }
    if (!selectedUser.userId) {
      showSnackbar("error", "ID utilisateur introuvable.");
      return;
    }

    try {
      setIsSaving(true);
      const raw = await assignAttributionRole(
        selectedUser.userId,
        selectedRole.idRole ?? selectedRole.id,
      );

      // ── Cast sécurisé — miroir du fix Flutter ────────────────
      // rawResponse is Map ? rawResponse : { success: false, message: ... }
      const response: AssignResponse =
        raw && typeof raw === "object" && !Array.isArray(raw)
          ? raw
          : { success: false, message: `Réponse inattendue : ${typeof raw}` };

      if (response.success === true) {
        // ── Mise à jour locale — miroir de users[idx] = User(...) ─
        const updatedUser: UserType = {
  ...selectedUser,
  idRole: selectedRole.idRole ?? selectedRole.id,
  nom:    selectedRole.nom    ?? selectedRole.name,
};
        setUsers((prev) =>
          prev.map((u) => (u.userId === selectedUser.userId ? updatedUser : u))
        );
        closeModal();
        showSnackbar("success", response.message ?? "Rôle assigné avec succès.");
      } else {
        showSnackbar("error", response.message ?? "Échec de l'assignation.");
      }
    } catch (e: any) {
      showSnackbar("error", getErrorMessage(e, "Échec de l'assignation.").replace("Exception: ", ""));
    } finally {
      setIsSaving(false);
    }
  }, [selectedUser, selectedRole, showSnackbar, closeModal]);

  // ═══════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════

  // ── Vendeur : pas accès à cette page ──────────────────────
  if (isVendeur) {
    return (
      <div style={{ ...styles.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ textAlign: "center", color: C.gray500 }}>
          <span style={{ fontSize: 48 }}>🔒</span>
          <p style={{ marginTop: 12, fontSize: 16, fontWeight: 600 }}>Accès non autorisé</p>
          <p style={{ fontSize: 13 }}>Les vendeurs ne peuvent pas accéder à la gestion des rôles.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
     

      {/* ── Snackbar ── */}
      {snackbar && (
        <div style={{
          ...styles.snackbar,
          background: snackbar.type === "success" ? C.success : C.error,
        }}>
          <span style={styles.snackbarIcon}>
            {snackbar.type === "success" ? "✅" : "❌"}
          </span>
          {snackbar.message}
        </div>
      )}

      <main style={styles.main}>
        {/* ── Bandeau dépôt pour responsable boutique ── */}
        {isRespBoutique && currentDepot && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: `${C.primary}12`,
            border: `1.5px solid ${C.primary}33`,
            borderRadius: 10,
            padding: "9px 14px",
            marginBottom: 14,
            fontSize: 13,
            color: C.primary,
            fontWeight: 600,
          }}>
            <span style={{ fontSize: 16 }}>🏪</span>
            <span>Dépôt : <strong>{currentDepot}</strong></span>
            <span style={{
              marginLeft: "auto",
              fontSize: 11,
              color: C.gray500,
              fontWeight: 400,
            }}>
              Affichage filtré par votre dépôt
            </span>
          </div>
        )}

        {/* ── Loading ── */}
        {isLoading && (
          <div style={styles.centered}>
            <div style={styles.spinner} />
            <p style={styles.loadingText}>Chargement…</p>
          </div>
        )}

        {/* ── Erreur ── */}
        {!isLoading && errorMessage && (
          <div style={styles.centered}>
            <span style={{ fontSize: 40 }}>📡</span>
            <p style={styles.errorText}>{errorMessage}</p>
            <button style={styles.retryBtn} onClick={fetchData}>
              🔄 Réessayer
            </button>
          </div>
        )}

        {/* ── Contenu principal ── */}
        {!isLoading && !errorMessage && (
          <>
            {/* Barre de recherche */}
            <SearchBar value={searchQuery} onChange={setSearchQuery} />

            {/* Liste déroulante des utilisateurs */}
            {filteredUsers.length === 0 ? (
              <p style={styles.emptyText}>
                {searchQuery.length >= 3
                  ? "Aucun résultat pour cette recherche."
                  : "Aucun utilisateur trouvé."}
              </p>
            ) : (
              <div style={styles.listContainer}>
                {filteredUsers.map((user) => (
                  <UserCard
                    key={user.userId ?? user.username}
                    user={user}
                    onClick={() => openUserDetail(user)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* ── Modal détail utilisateur ── */}
      {modalOpen && selectedUser && (
        <Modal onClose={closeModal}>
          <UserDetailModal
            user={selectedUser}
            roles={roles}
            selectedRole={selectedRole}
            onRoleChange={setSelectedRole}
            onConfirm={assignRole}
            onCancel={closeModal}
            isSaving={isSaving}
          />
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SOUS-COMPOSANTS
// ═══════════════════════════════════════════════════════════════

// ── Barre de recherche ─────────────────────────────────────────
interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
}

function SearchBar({ value, onChange }: SearchBarProps) {
  return (
    <div style={styles.searchWrap}>
      <span style={styles.searchIcon}>🔍</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Rechercher (min. 3 caractères)…"
        style={styles.searchInput}
      />
      {value && (
        <button style={styles.clearBtn} onClick={() => onChange("")}>
          ✕
        </button>
      )}
    </div>
  );
}

// ── Card utilisateur (ligne cliquable) ────────────────────────
interface UserCardProps {
  user: UserType;
  onClick: () => void;
}

function UserCard({ user, onClick }: UserCardProps) {
  const roleName =
  user.nom?.trim() || user.roleName?.trim() || "Sans rôle";
  const displayName = user.fullName ?? user.username ?? "Utilisateur";
  const isActive = user.isActive === 1 || user.isActive === true;
  const depotLabel = (user.depot ?? user.depotName ?? user.idDepot ?? "").toString().trim();

  return (
    <button style={styles.card} onClick={onClick}>
      {/* Avatar */}
      <div style={styles.avatarWrap}>
        {user.photoUrl ? (
          <img src={user.photoUrl} alt={displayName} style={styles.avatar} />
        ) : (
          <span style={styles.avatarFallback}>
            {displayName.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* Infos */}
      <div style={styles.cardBody}>
        <span style={styles.cardName}>{displayName}</span>
        <span style={styles.cardRole}>{roleName}</span>
        {depotLabel ? (
          <span style={{
            fontSize: 11,
            color: "#184B8C",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 1,
          }}>
            🏪 {depotLabel}
          </span>
        ) : null}
      </div>

      {/* Statut + flèche */}
      <div style={styles.cardRight}>
        <span style={{
          ...styles.dot,
          background: isActive ? C.success : C.error,
        }} />
        <span style={styles.chevron}>›</span>
      </div>
    </button>
  );
}

// ── Overlay modal ──────────────────────────────────────────────
interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
}

function Modal({ children, onClose }: ModalProps) {
  // Fermer en cliquant sur l'overlay
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modalBox}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ── Contenu du modal : détail user + sélecteur rôle ──────────
interface UserDetailModalProps {
  user: UserType;
  roles: RoleType[];
  selectedRole: RoleType | null;
  onRoleChange: (role: RoleType | null) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

function UserDetailModal({
  user,
  roles,
  selectedRole,
  onRoleChange,
  onConfirm,
  onCancel,
  isSaving,
}: UserDetailModalProps) {
  const displayName = user.fullName ?? user.username ?? "Utilisateur";
  const currentRoleName = user.nom?.trim() || user.roleName?.trim() || "Sans rôle";
  const depotLabel = (user.depot ?? user.depotName ?? user.idDepot ?? "").toString().trim();

  return (
    <>
      {/* En-tête modal */}
      <div style={styles.modalHeader}>
        <div style={styles.modalAvatar}>
          {user.photoUrl ? (
            <img src={user.photoUrl} alt={displayName} style={styles.modalAvatarImg} />
          ) : (
            <span style={styles.modalAvatarLetter}>
              {displayName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div style={styles.modalUserInfo}>
          <p style={styles.modalName}>{displayName}</p>
          <p style={styles.modalUsername}>@{user.username ?? "—"}</p>
          {user.email && (
            <p style={styles.modalEmail}>{user.email}</p>
          )}
          {depotLabel ? (
            <p style={{
              margin: "3px 0 0",
              fontSize: 12,
              color: "#184B8C",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}>
              🏪 Dépôt : {depotLabel}
            </p>
          ) : null}
        </div>
        <button style={styles.closeBtn} onClick={onCancel}>✕</button>
      </div>

      {/* Rôle actuel */}
      <div style={styles.modalSection}>
        <label style={styles.sectionLabel}>Rôle actuel</label>
        <div style={styles.currentRoleBadge}>
          <span style={styles.badgeIcon}>🏷️</span>
          <span style={styles.badgeText}>{currentRoleName}</span>
        </div>
      </div>

      {/* Sélecteur nouveau rôle */}
      <div style={styles.modalSection}>
        <label style={styles.sectionLabel} htmlFor="role-select">
          Nouveau rôle
        </label>
        <div style={styles.selectWrap}>
          <select
            id="role-select"
            value={selectedRole?.idRole ?? selectedRole?.id ?? ""}
            onChange={(e) => {
              const r = roles.find(
                (x) => (x.idRole ?? x.id) === e.target.value
              );
              onRoleChange(r ?? null);
            }}
            style={styles.select}
          >
            <option value="" disabled>— Choisir un rôle —</option>
            {roles.map((r) => (
              <option key={r.idRole ?? r.id} value={r.idRole ?? r.id}>
                {r.nom ?? r.name}
              </option>
            ))}
          </select>
          <span style={styles.selectArrow}>▾</span>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.modalActions}>
        <button
          style={styles.cancelBtn}
          onClick={onCancel}
          disabled={isSaving}
        >
          Annuler
        </button>
        <button
          style={{
            ...styles.confirmBtn,
            opacity: isSaving || !selectedRole ? 0.65 : 1,
            cursor: isSaving || !selectedRole ? "not-allowed" : "pointer",
          }}
          onClick={onConfirm}
          disabled={isSaving || !selectedRole}
        >
          {isSaving ? (
            <>
              <span style={styles.btnSpinner} /> Enregistrement…
            </>
          ) : (
            "✔ Valider"
          )}
        </button>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// STYLES (inline — aucune dépendance externe)
// ═══════════════════════════════════════════════════════════════
const styles: { [key: string]: CSSProperties } = {
  // Page
  page: {
    minHeight: "100vh",
    background: C.gray100,
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    color: C.gray900,
    position: "relative",
  },

  // Header
  header: {
    background: C.primary,
    padding: "0 24px",
    height: 56,
    display: "flex",
    alignItems: "center",
    boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  headerInner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "0 auto",
    width: "100%",
    maxWidth: 720,
  },
  headerIcon: { fontSize: 22 },
  headerTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: C.white,
    letterSpacing: 0.2,
  },

  // Main
  main: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "20px 16px 80px",
  },

  // Centered (loading / error)
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    minHeight: 260,
  },
  spinner: {
    width: 38,
    height: 38,
    border: `4px solid ${C.gray200}`,
    borderTop: `4px solid ${C.primary}`,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  loadingText: { color: C.gray500, fontSize: 14 },
  errorText:   { color: C.error, fontSize: 14, textAlign: "center", maxWidth: 300 },
  emptyText:   { color: C.gray500, fontSize: 14, textAlign: "center", marginTop: 40 },

  retryBtn: {
    background: C.primary,
    color: C.white,
    border: "none",
    borderRadius: 8,
    padding: "10px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },

  // Snackbar
  snackbar: {
    position: "fixed",
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
    color: C.white,
    borderRadius: 10,
    padding: "12px 22px",
    fontSize: 14,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 18px rgba(15,23,42,0.22)",
    zIndex: 9999,
    minWidth: 260,
    maxWidth: "90vw",
    textAlign: "center",
    justifyContent: "center",
  },
  snackbarIcon: { fontSize: 16 },

  // Search
  searchWrap: {
    display: "flex",
    alignItems: "center",
    background: C.white,
    border: `1.5px solid ${C.gray200}`,
    borderRadius: 12,
    padding: "0 12px",
    marginBottom: 16,
    height: 44,
    gap: 8,
    boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
  },
  searchIcon: { fontSize: 16, color: C.gray500 },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: 14,
    color: C.gray900,
    background: "transparent",
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: C.gray500,
    fontSize: 14,
    padding: 0,
    lineHeight: 1,
  },

  // List
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  // User card
  card: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: C.white,
    border: "none",
    borderRadius: 14,
    padding: "12px 14px",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
    transition: "box-shadow 0.15s ease",
  },
  avatarWrap: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    overflow: "hidden",
    flexShrink: 0,
    background: `${C.primary}20`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatar: { width: "100%", height: "100%", objectFit: "cover" },
  avatarFallback: {
    fontSize: 20,
    fontWeight: 700,
    color: C.primary,
  },
  cardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },
  cardName: {
    fontSize: 15,
    fontWeight: 600,
    color: C.gray900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardRole: {
    fontSize: 13,
    color: C.gray500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    display: "inline-block",
  },
  chevron: {
    fontSize: 22,
    color: C.gray500,
    lineHeight: 1,
  },

  // Overlay
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    padding: 16,
  },
  modalBox: {
    background: C.white,
    borderRadius: 18,
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 16px 48px rgba(15,23,42,0.22)",
    overflow: "hidden",
  },

  // Modal header
  modalHeader: {
    background: C.primaryLt,
    padding: "20px 20px 16px",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    borderBottom: `1.5px solid ${C.gray200}`,
    position: "relative",
  },
  modalAvatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    overflow: "hidden",
    flexShrink: 0,
    background: `${C.primary}22`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `2px solid ${C.primary}33`,
  },
  modalAvatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  modalAvatarLetter: {
    fontSize: 22,
    fontWeight: 700,
    color: C.primary,
  },
  modalUserInfo: { flex: 1, minWidth: 0 },
  modalName: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: C.gray900,
  },
  modalUsername: {
    margin: "2px 0 0",
    fontSize: 13,
    color: C.gray500,
  },
  modalEmail: {
    margin: "2px 0 0",
    fontSize: 12,
    color: C.gray500,
  },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    background: "none",
    border: "none",
    fontSize: 18,
    color: C.gray500,
    cursor: "pointer",
    lineHeight: 1,
    padding: 4,
  },

  // Modal sections
  modalSection: {
    padding: "16px 20px 0",
  },
  sectionLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: C.gray500,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  currentRoleBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: `${C.primary}15`,
    border: `1px solid ${C.primary}33`,
    borderRadius: 8,
    padding: "7px 14px",
  },
  badgeIcon: { fontSize: 15 },
  badgeText: {
    fontSize: 14,
    fontWeight: 600,
    color: C.primary,
  },

  // Select
  selectWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  select: {
    width: "100%",
    appearance: "none",
    WebkitAppearance: "none",
    background: C.white,
    border: `1.5px solid ${C.gray200}`,
    borderRadius: 10,
    padding: "10px 36px 10px 14px",
    fontSize: 14,
    color: C.gray900,
    cursor: "pointer",
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.15s ease",
  },
  selectArrow: {
    position: "absolute",
    right: 12,
    color: C.gray500,
    pointerEvents: "none",
    fontSize: 14,
  },

  // Modal actions
  modalActions: {
    display: "flex",
    gap: 10,
    padding: "20px",
    justifyContent: "flex-end",
  },
  cancelBtn: {
    background: C.gray100,
    border: `1.5px solid ${C.gray200}`,
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    color: C.gray700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  confirmBtn: {
    background: C.primary,
    border: "none",
    borderRadius: 10,
    padding: "10px 22px",
    fontSize: 14,
    fontWeight: 600,
    color: C.white,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontFamily: "inherit",
    transition: "opacity 0.15s ease",
  },
  btnSpinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: `2px solid rgba(255,255,255,0.4)`,
    borderTop: `2px solid ${C.white}`,
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
};

// Injecter l'animation CSS keyframe spin
if (typeof document !== "undefined" && !document.getElementById("ar-spin")) {
  const style = document.createElement("style");
  style.id = "ar-spin";
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    button[style*="cursor: pointer"]:hover {
      filter: brightness(0.96);
    }
  `;
  document.head.appendChild(style);
}